import { useEffect, useState } from "react";
import { Trophy, Crown, Shield, Network, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { qualifyField, buildBracket, MAX_FIELD } from "@/lib/showdownBracket";

const NAMES = ["Mike Mawson", "Caleb", "charlie", "Gael", "Justin Smith", "Nick", "Preston", "Ricky",
  "Player 9", "Player 10", "Player 11", "Player 12", "Player 13", "Player 14", "Player 15", "Player 16",
  "Player 17", "Player 18", "Player 19", "Player 20"];

function mockStandings(n) {
  return Array.from({ length: n }, (_, i) => ({
    user_id: "m" + i, display_name: NAMES[i] || `Player ${i + 1}`,
    wins: n - i, losses: i, ties: 0, points: (n - i) * 10,
  }));
}

function Seed({ p, win }) {
  if (!p) return <div className="text-zinc-600 text-xs">TBD</div>;
  return (
    <div className={`flex items-center gap-2 text-xs ${win ? "text-emerald-400 font-bold" : "text-zinc-300"}`}>
      <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-black ${win ? "bg-emerald-500/20 text-emerald-300" : "bg-[#1A1A1D] text-zinc-400"}`}>{p.seed ?? "-"}</span>
      <span className="truncate">{p.display_name}</span>
    </div>
  );
}

function Game({ g, testid }) {
  if (!g) return null;
  const wSeed = g.winner?.seed;
  const hasPts = g.points_a != null && g.points_b != null;
  return (
    <div data-testid={testid} className="rounded-lg border border-[#1F1F22] bg-[#0F0F10] p-2 space-y-1">
      <div className="flex items-center justify-between">
        <Seed p={g.a} win={g.a && g.a.seed === wSeed} />
        {hasPts && <span className="text-[10px] text-zinc-500">{g.points_a}</span>}
      </div>
      <div className="text-[9px] text-zinc-600 pl-1">vs</div>
      <div className="flex items-center justify-between">
        <Seed p={g.b} win={g.b && g.b.seed === wSeed} />
        {hasPts && <span className="text-[10px] text-zinc-500">{g.points_b}</span>}
      </div>
    </div>
  );
}

function RoundCol({ title, children, testid }) {
  return (
    <div className="min-w-[170px] flex-1" data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-2">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

// Renders from a canonical bracket object (frontend buildBracket OR backend payload).
export function PlayoffBracket({ bracket, overflow = [] }) {
  if (!bracket) return null;
  if (bracket.error) {
    return <div data-testid="playoff-error" className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200">{bracket.error}</div>;
  }
  const over = overflow.length ? overflow : (bracket.overflow || []);
  return (
    <div data-testid="playoff-bracket" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400">
        <span><b className="text-white">{bracket.n}</b> qualifiers</span>
        <span>· <b className="text-white">{bracket.byeCount}</b> Wild Card bye{bracket.byeCount === 1 ? "" : "s"}</span>
        <span>· <b className="text-white">{bracket.wildcard.length}</b> Wild Card game{bracket.wildcard.length === 1 ? "" : "s"}</span>
        {over.length > 0 && <span className="text-red-300">· {over.length} outside field (top 16 only)</span>}
      </div>

      <div>
        <div className="text-[10px] font-black uppercase tracking-wider text-[#ff7a7a] mb-2">Round 1 · Wild Card Weekend</div>
        {bracket.byeCount > 0 && (
          <div data-testid="playoff-byes" className="mb-2 text-xs text-zinc-400">
            Byes to top {bracket.byeCount}: {bracket.byes.map((b) => `#${b.seed} ${b.display_name}`).join(", ")}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="playoff-wildcard">
          {bracket.wildcard.length === 0 && <div className="text-xs text-zinc-500">No Wild Card games — all {bracket.n} players receive byes and advance to the Divisional Round.</div>}
          {bracket.wildcard.map((g, i) => <Game key={i} g={g} testid={`wc-game-${i}`} />)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[bracket.sideA, bracket.sideB].map((side, si) => (
          <div key={si} data-testid={`playoff-side-${si === 0 ? "a" : "b"}`} className="rounded-xl border border-[#1F1F22] bg-[#0A0A0A] p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Shield className="h-3.5 w-3.5 text-[#4C8DFF]" />
              <span className="text-xs font-black uppercase tracking-wider">{side.name}</span>
            </div>
            <div className="flex gap-2">
              <RoundCol title="Round 2 · Divisional" testid={`div-${si}`}>
                <Game g={side.div[0]} testid={`div-${si}-0`} />
                <Game g={side.div[1]} testid={`div-${si}-1`} />
              </RoundCol>
              <RoundCol title="Round 3 · Conf. Championship" testid={`conf-${si}`}>
                <Game g={side.conf} testid={`conf-${si}-0`} />
              </RoundCol>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-[#D50A0A]/40 bg-[#D50A0A]/5 p-4" data-testid="playoff-superbowl">
        <div className="flex items-center gap-1.5 mb-2">
          <Trophy className="h-4 w-4 text-[#ff5b5b]" />
          <span className="text-xs font-black uppercase tracking-wider">Round 4 · PickHouse Super Bowl</span>
        </div>
        <div className="max-w-xs"><Game g={bracket.superBowl} testid="sb-game" /></div>
        <div className="mt-3 flex items-center gap-2" data-testid="playoff-champion">
          <Crown className="h-4 w-4 text-amber-400" />
          <span className="text-sm">Champion: <b className="text-amber-300">{bracket.champion ? `#${bracket.champion.seed} ${bracket.champion.display_name}` : "TBD"}</b></span>
        </div>
      </div>
    </div>
  );
}

export function IfPlayoffsToday({ field, viewerSeed }) {
  const n = field.length;
  const byeCount = Math.max(0, MAX_FIELD - n);
  const inField = viewerSeed >= 1 && viewerSeed <= n;
  const hasBye = inField && viewerSeed <= byeCount;
  const opp = inField && !hasBye ? field.find((p) => p.seed === byeCount + 1 + n - viewerSeed) : null;
  const viewer = field[viewerSeed - 1];
  return (
    <div data-testid="if-playoffs-today" className="rounded-2xl border border-[#4C8DFF]/30 bg-[#4C8DFF]/5 p-5">
      <div className="flex items-center gap-2 mb-2">
        <Trophy className="h-4 w-4 text-[#4C8DFF]" />
        <span className="overline text-[#7ab0ff]">If the playoffs started today</span>
      </div>
      <p className="text-sm">
        {viewer && <>Viewing as <b>{viewer.display_name}</b> · </>}
        {inField
          ? <span className="text-emerald-400 font-bold" data-testid="proj-in">IN PLAYOFF FIELD · Seed #{viewerSeed}</span>
          : <span className="text-red-400 font-bold" data-testid="proj-out">OUTSIDE PLAYOFF FIELD</span>}
      </p>
      {inField && (
        <p className="text-sm text-zinc-400 mt-1">
          {hasBye
            ? <span className="text-purple-300 font-semibold" data-testid="proj-bye">First-round bye (Wild Card)</span>
            : <>Projected Wild Card matchup: <b className="text-white">#{viewerSeed}</b> vs <b className="text-white">#{opp?.seed} {opp?.display_name}</b></>}
        </p>
      )}
      <p className="text-[11px] text-zinc-600 mt-2">Informational only — no playoff records, matchups, or standings are created.</p>
    </div>
  );
}

// Commissioner mock preview: pure client-side, ZERO DB writes.
export function PlayoffPreview() {
  const [size, setSize] = useState(16);
  const [simWeek, setSimWeek] = useState(14);
  const [viewSeed, setViewSeed] = useState(6);
  const standings = mockStandings(size);
  const field = qualifyField(standings);
  const bracket = buildBracket(field);
  const overflow = standings.slice(MAX_FIELD);

  return (
    <div className="mt-4 rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-5" data-testid="playoff-preview">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-300 text-[10px] font-black uppercase px-2 py-0.5 mb-2">Mock Preview · not real data</div>
      <p className="text-xs text-zinc-500 mb-3 max-w-2xl">
        Test-only playoff preview using MOCK standings. Writes NOTHING to the database. Winners are mocked as the higher seed. Hail Mary is regular-season only and unavailable in every playoff round.
      </p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Mock field size</label>
          <select data-testid="playoff-field-select" value={size} onChange={(e) => { const v = +e.target.value; setSize(v); setViewSeed(Math.min(viewSeed, v)); }}
            className="rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2 text-sm">
            {[20, 16, 15, 14, 12, 10, 8, 6].map((v) => <option key={v} value={v}>{v} players{v > 16 ? " (top-16 cut)" : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Simulated week</label>
          <select data-testid="playoff-week-select" value={simWeek} onChange={(e) => setSimWeek(+e.target.value)}
            className="rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2 text-sm">
            {[10, 11, 12, 13, 14, 15, 16, 17, 18].map((w) => <option key={w} value={w}>Week {w}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">View projection as seed</label>
          <select data-testid="playoff-viewseed-select" value={viewSeed} onChange={(e) => setViewSeed(+e.target.value)}
            className="rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2 text-sm">
            {Array.from({ length: size }, (_, i) => i + 1).map((s) => <option key={s} value={s}>Seed #{s}</option>)}
          </select>
        </div>
      </div>

      {simWeek >= 14 ? (
        <div className="mb-4"><IfPlayoffsToday field={field} viewerSeed={viewSeed} /></div>
      ) : (
        <div className="mb-4 text-xs text-zinc-500" data-testid="proj-hidden">"If the playoffs started today" is hidden before Regular Season Week 14 (currently simulating Week {simWeek}).</div>
      )}

      <PlayoffBracket bracket={bracket} overflow={overflow} />
    </div>
  );
}

// ---- REAL wiring (consumes live backend endpoints; scoped by league+season) ----

// Real Week 14–18 player projection from REAL league standings. Hidden otherwise.
export function RealProjection() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/showdown/playoffs/projection").then(({ data }) => setP(data)).catch(() => {}); }, []);
  if (!p || !p.active) return null;
  return (
    <div className="mb-8" data-testid="real-projection">
      <IfPlayoffsToday field={p.field} viewerSeed={p.viewer_seed} />
    </div>
  );
}

// Real persisted playoff bracket + Commissioner generate action.
export function RealPlayoffs({ isAdmin }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/showdown/playoffs").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!window.confirm("Generate the real PickHouse Showdown playoff bracket from final Regular Season standings?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/showdown/playoffs/generate", {});
      toast.success(`Playoffs generated · ${data.qualifiers} qualifiers · ${data.byes} byes · ${data.wildcard_games} Wild Card games`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  if (data.exists) {
    return (
      <div className="mb-8 rounded-2xl border border-[#4C8DFF]/30 bg-[#0A0A0A] p-5" data-testid="real-playoffs">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-black uppercase px-2 py-0.5 mb-2">Live Playoffs</div>
        {data.my_seed && <p className="text-sm mb-2">Your seed: <b className="text-white">#{data.my_seed}</b></p>}
        <PlayoffBracket bracket={data.bracket} />
      </div>
    );
  }
  if (!isAdmin) return null;
  return (
    <div className="mb-8 rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-5" data-testid="real-playoffs-generate">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="h-4 w-4 text-[#4C8DFF]" />
        <h2 className="font-display text-sm font-bold uppercase tracking-wider">PickHouse Playoffs</h2>
      </div>
      <button data-testid="generate-playoffs-btn" onClick={generate} disabled={busy || !data.can_generate}
        className="mt-2 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#4C8DFF] text-white font-bold active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />} Generate Showdown Playoffs
      </button>
      {!data.can_generate && data.generate_reason && (
        <p className="text-xs text-zinc-500 mt-2" data-testid="generate-blocked-reason">{data.generate_reason}</p>
      )}
    </div>
  );
}

// Commissioner-only: routes MOCK data through the REAL backend build function.
export function RealWiringTest() {
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState(12);
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (n) => {
    setBusy(true);
    try { const { data } = await api.get(`/showdown/playoffs/preview-build?size=${n}`); setPayload(data.bracket); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4" data-testid="real-wiring-test">
      <button data-testid="real-wiring-toggle" onClick={() => { const v = !open; setOpen(v); if (v && !payload) run(size); }}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-xs font-semibold text-emerald-200 hover:border-emerald-400 transition-colors">
        <Network className="h-4 w-4" /> {open ? "Hide REAL-wiring test" : "REAL-wiring test (production functions)"}
      </button>
      {open && (
        <div className="mt-3 rounded-2xl border border-emerald-500/20 bg-[#0A0A0A] p-5">
          <p className="text-xs text-zinc-500 mb-3 max-w-2xl">Routes MOCK standings through the REAL backend bracket engine (<code>/showdown/playoffs/preview-build</code>) and renders it through the same component the live bracket uses. ZERO database writes.</p>
          <div className="flex items-end gap-3 mb-4">
            <div>
              <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Mock field size</label>
              <select data-testid="real-wiring-size" value={size} onChange={(e) => { const v = +e.target.value; setSize(v); run(v); }}
                className="rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2 text-sm">
                {[20, 16, 12, 10, 8, 6].map((v) => <option key={v} value={v}>{v} players</option>)}
              </select>
            </div>
            {busy && <Loader2 className="h-4 w-4 animate-spin text-emerald-400 mb-2" />}
          </div>
          {payload && <PlayoffBracket bracket={payload} />}
        </div>
      )}
    </div>
  );
}
